import {
  GeneralConfiguration,
  GoogleConfiguration,
  MeasurementUnitType,
  Action as PermissionAction,
  ResourceName,
  Tenant,
  User,
  UserPreferences,
  WithNull,
} from '@mti/multitenant-ui-shared';
import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { LogoutRequest, TabType } from '../Header/types';
import {
  AuthErrorMessage,
  AuthorizationDetails,
  ContainerState,
  MOCK_TENANT_ID,
  TenantIDWithExtension,
} from './types';

// The initial state of login feature
export const initialState: ContainerState = {
  isAuthenticated: false,
  userProfile: {
    id: '',
    accessToken: '',
    organizationId: '',
    email: '',
    name: '',
    tenantId: '',
    operatorExtension: '',
    expiresOn: 0,
  },
  userPermissions: [],
  authDetails: {
    telOpAccess: false,
    queryAccess: false,
    adminAccess: false,
    superAdminAccess: false,
    supervisorAccess: false,
    messagingAccess: false,
    mapAccess: false,
  },
  userCDNs: [],
  userPreferences: {},
  tenant: {
    tenantId: '',
    companyName: '',
    generalConfiguration: {
      id: -1,
      tenantId: '',
      vehicleCustomFieldsToDisplay: '',
      driverCustomFieldsToDisplay: '',
      measurementUnitType: MeasurementUnitType.Metric,
    },
    googleConfiguration: {
      id: -1,
      tenantId: '',
      showDirections: true,
      showSnapToRoads: true,
      allowAddressLookup: true,
    },
    dispatchSystems: [],
  },
  selectedTab: TabType.Book,
  processing: false,
  fetchingPermissions: false,
  error: undefined,
  operatorPulseDispatch: { processing: false, success: false },
  testsEnabled: false,
};

const authSlice = createSlice({
  name: 'authState',
  initialState,
  reducers: {
    login(state, _action: PayloadAction<TenantIDWithExtension>) {
      state.processing = true;
      state.isAuthenticated = false;
      state.error = undefined;
    },
    loginSuccess(
      state,
      action: PayloadAction<{ user: User; tenant: Tenant; refresh: boolean }>,
    ) {
      const testing = action.payload?.tenant?.tenantId === MOCK_TENANT_ID;
      state.testsEnabled = testing;
      state.userProfile = action.payload.user.userProfile;
      state.userPermissions = action.payload.user.userPermissions;
      state.authDetails = {
        telOpAccess:
          testing ||
          state.userPermissions.some(
            permission =>
              permission.action === PermissionAction.Access &&
              permission.resourceName === ResourceName.TelOp,
          ),
        queryAccess:
          testing ||
          state.userPermissions.some(
            permission =>
              permission.action === PermissionAction.Access &&
              permission.resourceName === ResourceName.Query,
          ),
        adminAccess:
          testing ||
          state.userPermissions.some(
            permission =>
              permission.action === PermissionAction.Access &&
              permission.resourceName === ResourceName.Admin,
          ),
        superAdminAccess:
          !testing &&
          state.userPermissions.some(
            permission =>
              permission.action === PermissionAction.Access &&
              permission.resourceName === ResourceName.SuperAdmin,
          ),
        supervisorAccess:
          testing ||
          state.userPermissions.some(
            permission =>
              permission.action === PermissionAction.Access &&
              permission.resourceName === ResourceName.Supervisor,
          ),
        messagingAccess:
          testing ||
          state.userPermissions.some(
            permission =>
              permission.action === PermissionAction.Access &&
              permission.resourceName === ResourceName.Messaging,
          ),
        mapAccess:
          testing ||
          state.userPermissions.some(
            permission =>
              permission.action === PermissionAction.Access &&
              permission.resourceName === ResourceName.Map,
          ),
      };
      state.userCDNs = action.payload.user.userCDNs;
      // Use JSON parse/stringify to deep clone and remove readonly constraints
      state.userPreferences = JSON.parse(
        JSON.stringify(action.payload.user.userPreferences ?? {}),
      );
      state.tenant = action.payload.tenant;
      state.isAuthenticated = true;
      state.processing = false;
      state.fetchingPermissions = false;
      if (!action.payload.refresh) {
        state.selectedTab = state.authDetails.telOpAccess
          ? TabType.Book
          : state.authDetails.queryAccess || state.authDetails.supervisorAccess
            ? TabType.Dispatch
            : state.authDetails.mapAccess
              ? TabType.Map
              : state.authDetails.adminAccess ||
                  state.authDetails.superAdminAccess
                ? TabType.Admin
                : TabType.Book;
      }
      state.error = undefined;
    },
    loginError(state, action: PayloadAction<AuthErrorMessage | undefined>) {
      state.isAuthenticated = false;
      state.processing = false;
      state.fetchingPermissions = false;
      state.error = action.payload;
    },
    logout(_state, _action: PayloadAction<LogoutRequest | undefined>) {},
    logoutSuccess(state) {
      state.isAuthenticated = false;
      state.processing = false;
      state.fetchingPermissions = false;
      state.error = undefined;
    },
    checkAuth(
      state,
      action: PayloadAction<{ tenantId: string | undefined; refresh: boolean }>,
    ) {
      if (!action.payload.refresh) state.processing = true;
      state.error = undefined;
    },
    setAuthorizationDetails(
      state,
      action: PayloadAction<AuthorizationDetails>,
    ) {
      state.authDetails = action.payload;
    },
    setSelectedTab(state, action: PayloadAction<TabType>) {
      if (state.selectedTab !== action.payload)
        state.selectedTab = action.payload;
    },
    setOperatorPulse(
      state,
      _action: PayloadAction<{
        id: string;
        tenantId: string;
        accessToken: string;
      }>,
    ) {
      state.operatorPulseDispatch.processing = true;
    },
    setOperatorPulseSuccess(state, action: PayloadAction<{ error: boolean }>) {
      state.operatorPulseDispatch.processing = false;
      state.operatorPulseDispatch.success = !action.payload.error;
    },
    setFetchingPermissions(
      state,
      action: PayloadAction<{ fetching: boolean }>,
    ) {
      state.fetchingPermissions = action.payload.fetching;
    },
    updateUserPreferences(
      state,
      action: PayloadAction<{
        tenantId: string;
        operatorEmail: string;
        accessToken: string;
        userPreferences: WithNull<Partial<UserPreferences>>;
      }>,
    ) {
      // Merge in the new preferences, but set the ones with null values to undefined
      const newPrefs: Partial<UserPreferences> = {
        ...state.userPreferences,
        ...Object.fromEntries(
          Object.entries(action.payload.userPreferences).map(([key, value]) => [
            key,
            value === null ? undefined : value,
          ]),
        ),
      };

      // Use JSON parse/stringify to deep clone and remove readonly constraints
      state.userPreferences = JSON.parse(JSON.stringify(newPrefs));
    },
    setTenantGeneralConfiguration(
      state,
      action: PayloadAction<GeneralConfiguration>,
    ) {
      state.tenant.generalConfiguration = action.payload;
    },
    setTenantGoogleConfiguration(
      state,
      action: PayloadAction<GoogleConfiguration>,
    ) {
      state.tenant.googleConfiguration = action.payload;
    },
  },
});

export const { actions, reducer, name: sliceKey } = authSlice;
